import { account } from '../core/account.js';
import { profiles } from '../core/profiles.js';
import { t } from '../core/i18n.js';
import { escapeHtml } from '../core/utils.js';
import { notify } from '../core/notify.js';
import { addPasswordToggles } from '../core/pwtoggle.js';
import { providerType } from '../core/clients.js';
import { prepareProvider, verifyProvider } from '../core/connect.js';
import { storageBridge, startBridge, storageAvailable, smbHost, parseSmbHost, newLocalHost, cloudHost, cloudPath, isCloud } from '../core/storage-bridge.js';
import { device } from '../core/device.js';
import { DROPBOX_KEY, MS_CLIENT_ID } from '../core/flavor.js';
import { dropboxStart, dropboxExchange, dropboxAccount, msDeviceStart, msDeviceWait, msAccount, msAuthStart, msAuthExchange, loopbackUri, loopbackAvailable, randomState, waitForCallback } from '../core/cloud-auth.js';
import { SOURCE_GROUPS, SECTIONS, availableGroups, availableTypes, helpOptions, isClassic } from '../core/storage-sources.js';
import { ART } from './add-source-art.js';

/* Asistente «Añadir fuente»: ¿dónde están tus vídeos? → ¿de qué tipo? → los datos. Así la primera pantalla no tiene que
   enseñarlo todo: aquí se explican las opciones una a una. Los tipos de siempre (Jellyfin, Emby, Plex, WebDAV, listas)
   abren el formulario de siempre con el tipo ya elegido; SMB y las carpetas del aparato tienen pantalla propia. */

/** Mensaje para un fallo al comprobar un origen de almacenamiento (SMB o carpeta del aparato). */
export function storageErrorKey(err) {
    const status = err && err.status;
    if (err && err.code === 'fields') return 'addsrc.err.fields';
    if (err && err.code === 'auth') return err.cloud ? 'addsrc.err.cloudauth' : 'addsrc.err.auth';
    if (err && err.code === 'empty') return 'addsrc.err.empty';
    if (err && err.code === 'notfound') return 'addsrc.err.notfound';
    if (err && (err.code === 'code' || err.code === 'expired' || err.code === 'declined' || err.code === 'offline' || err.code === 'unconfigured')) return 'addsrc.err.' + err.code;
    if (err && err.code === 'cancelled') return '';
    if (err && err.code === 'pick') return 'addsrc.err.pick';
    if (status === 404) return 'addsrc.err.notfound';
    if (status === 502 || status === 504 || status === 0 || (err && err.code === 'unreachable')) return 'addsrc.err.unreachable';
    return 'addsrc.err.generic';
}

/** Tarjeta del asistente: icono de color según el grupo, título, explicación, dibujo de fondo (solo en la primera pantalla) y flecha. */
/** Clave de texto de un grupo. Sin servidor local (la web), «En mi red» son servidores publicados en internet: otro título y otra explicación. */
const gk = (id, suffix = '') => 'addsrc.group.' + id + suffix + (id === 'network' && !storageAvailable('smb') ? (suffix === '' || suffix === '.sub' || suffix === '.step' ? '.web' : '') : '');

export const card = (attrs, { group, icon, title, sub, art = '', help = false }) => `<button type="button" class="wiz-card ${help ? 'is-help' : ''}" data-focusable data-g="${group}" ${attrs}>
    <span class="wiz-tile"><span class="mi">${icon}</span></span>
    <span class="wiz-body"><b>${escapeHtml(title)}</b><small>${escapeHtml(sub)}</small></span>
    ${art ? `<span class="wiz-art" aria-hidden="true">${art}</span>` : ''}<span class="mi wiz-chev">chevron_right</span></button>`;

function groupsHtml(groups, canHelp) {
    const ids = groups.map((g) => g.id);
    const sections = SECTIONS.map((sec) => ({ sec, list: groups.filter((g) => sec.groups.indexOf(g.id) !== -1) })).filter((x) => x.list.length);
    // Si solo hay un grupo no hace falta separarlo en secciones.
    const body = sections.map(({ sec, list }) => `${ids.length > 1 ? `<div class="wiz-section">${t('addsrc.section.' + sec.id)}</div>` : ''}<div class="wiz-cards" data-nav-list>${list.map((g) => card(`data-group="${g.id}"`, { group: g.id, icon: g.icon, title: t(gk(g.id)), sub: t(gk(g.id, '.sub')), art: ART[g.id] || '' })).join('')}</div>`).join('');
    const help = canHelp ? `<div class="wiz-cards wiz-help" data-nav-list>${card('data-help-open', { group: 'help', icon: 'help_outline', title: t('addsrc.help.title'), sub: t('addsrc.help.sub'), help: true })}</div>` : '';
    return body + help;
}

/** Dibujo grande de arriba (el teléfono, el NAS, las nubes), que acompaña a las pantallas de un grupo. */
export const hero = (group) => (ART[group] ? `<div class="wiz-hero" data-g="${group}" aria-hidden="true">${ART[group]}</div>` : '');

function typesHtml(group, types) {
    const g = SOURCE_GROUPS.find((x) => x.id === group);
    const list = `<div class="wiz-cards" data-nav-list>${types.map((id) => card(`data-type="${id}"`, { group: id, icon: providerType(id).icon || g.icon, title: t('addsrc.type.' + id), sub: t('addsrc.type.' + id + '.sub') })).join('')}</div>`;
    // En la red hay mucho donde perderse: una ayuda a mano. En la nube, la garantía de qué se lee.
    const extra = group === 'network' ? `<div class="wiz-cards wiz-help" data-nav-list>${card('data-help-open', { group: 'help', icon: 'lightbulb', title: t('addsrc.netHelp.title'), sub: t('addsrc.netHelp.sub'), help: true })}</div>`
        : group === 'cloud' ? `<p class="wiz-note"><span class="mi">lock</span><span>${t('addsrc.cloud.note')}</span></p>` : '';
    return hero(group) + list + extra;
}

/** Un solo tipo en el grupo «En este aparato»: se va derecho a elegir la carpeta, sin pasos de más. */
function deviceHtml() {
    return `${hero('device')}<div class="wiz-cards" data-nav-list>${card('data-pick-direct', { group: 'local', icon: 'folder', title: t('addsrc.local.pick'), sub: t('addsrc.local.pickSub') })}</div>
        <p class="wiz-note"><span class="mi">movie</span><span>${t('addsrc.local.note')}</span></p>`;
}

function helpHtml(options) {
    return `<p class="type-hint">${t('addsrc.help.intro')}</p>
        <div class="wiz-cards" data-nav-list>${options.map((h) => card(`data-help="${h.id}"`, { group: h.group, icon: ({ device: 'smartphone', nas: 'dns', server: 'video_library', cloud: 'cloud', free: 'public', list: 'playlist_play' })[h.id], title: t('addsrc.help.' + h.id), sub: t('addsrc.help.' + h.id + '.sub') })).join('')}</div>`;
}

function smbFormHtml(v = {}) {
    return `<p class="type-hint">${t('addsrc.smb.hint')}</p>
        <div class="field"><label>${t('login.name')}</label><input name="name" value="${escapeHtml(v.name || '')}" placeholder="${t('addsrc.smb.namePh')}" autocomplete="off" data-nav-default/></div>
        <div class="field mt-2"><label>${t('addsrc.smb.server')}</label><input name="server" value="${escapeHtml(v.server || '')}" placeholder="192.168.1.20" autocomplete="off" autocapitalize="off" inputmode="url"/></div>
        <div class="field mt-2"><label>${t('addsrc.smb.share')}</label><input name="share" value="${escapeHtml(v.share || '')}" placeholder="${t('addsrc.smb.sharePh')}" autocomplete="off" autocapitalize="off"/><small class="field-help">${t('addsrc.smb.shareHelp')}</small></div>
        <div class="field mt-2"><label>${t('addsrc.smb.path')}</label><input name="path" value="${escapeHtml(v.path || '')}" placeholder="${t('addsrc.smb.pathPh')}" autocomplete="off" autocapitalize="off"/></div>
        <div class="field mt-2"><label>${t('login.user')}</label><input name="username" value="${escapeHtml(v.username || '')}" autocomplete="username" autocapitalize="off"/><small class="field-help">${t('addsrc.smb.userHelp')}</small></div>
        <div class="field mt-2"><label>${t('login.pass')}</label><input name="password" type="password" value="${escapeHtml(v.password || '')}" autocomplete="current-password"/></div>`;
}

function localFormHtml(v = {}) {
    return `<p class="type-hint">${t('addsrc.local.hint')}</p>
        <button type="button" class="btn btn-block" data-pick><span class="mi">folder_open</span>${t('addsrc.local.pick')}</button>
        <div class="wiz-picked" data-picked ${v.picked ? '' : 'hidden'}><span class="mi">check_circle</span><span data-picked-name>${escapeHtml(v.picked || '')}</span></div>
        <div class="field mt-2"><label>${t('login.name')}</label><input name="name" value="${escapeHtml(v.name || '')}" placeholder="${t('addsrc.local.namePh')}" autocomplete="off"/></div>`;
}

function cloudFormHtml(type, v = {}, editing = false, loop = false) {
    const dropbox = type === 'dropbox' && !loop; // con vuelta automática no hay código que pegar
    return `<p class="type-hint">${t('addsrc.' + type + (loop ? '.hintLoop' : '.hint'))}</p>
        ${editing ? '' : dropbox ? `<button type="button" class="btn btn-block" data-cloud-open><span class="mi">open_in_new</span>${t('addsrc.dropbox.open')}</button>
        <div class="field mt-2"><label>${t('addsrc.dropbox.code')}</label><input name="code" placeholder="${t('addsrc.dropbox.codePh')}" autocomplete="off" autocapitalize="off" spellcheck="false"/></div>` : ''}
        <div class="field mt-2"><label>${t('login.name')}</label><input name="name" value="${escapeHtml(v.name || '')}" placeholder="${t('addsrc.' + type + '.namePh')}" autocomplete="off"/></div>
        <div class="field mt-2"><label>${t('addsrc.cloud.folder')}</label><input name="folder" value="${escapeHtml(v.folder || '')}" placeholder="${t('addsrc.cloud.folderPh')}" autocomplete="off" autocapitalize="off"/><small class="field-help">${t('addsrc.cloud.folderHelp')}</small></div>
        ${loop && !editing ? `<button type="button" class="btn btn-ghost btn-block" data-cloud-manual>${t('addsrc.cloud.manual')}</button>` : ''}
        <div class="wiz-device" data-device hidden>
            <div data-device-codebox>
                <div class="wiz-device-label">${t('addsrc.onedrive.codeIs')}</div>
                <div class="pair-code wiz-device-code" data-device-code></div>
                <div class="wiz-device-url" data-device-url></div>
            </div>
            <div class="wiz-device-wait" data-device-wait></div>
            <div class="wiz-device-wait" data-device-state></div>
            <button type="button" class="btn btn-block" data-device-cancel>${t('common.cancel')}</button>
        </div>`;
}

/**
 * Abre el asistente en una ventana modal (a pantalla completa en el móvil, tapando la barra de abajo).
 * opts: { edit: id de un SMB, carpeta o nube que se edita, group + step: 'type' para empezar en la lista de un grupo }.
 */
export function openAddSource(opts = {}) {
    const app = window.app;
    if (!app) return null;
    if (account.demo) return null; // la demo pública es de solo lectura: no añade fuentes
    const id = 'wiz-' + Date.now().toString(36);
    let cleanup = () => {};
    let popup = null;
    popup = app.popup.create({
        content: `<div class="popup wiz-popup" id="${id}"><div class="page"><div class="page-content" style="padding:0"><div class="auth"><div class="auth-card form" data-wiz></div></div></div></div></div>`,
        on: {
            open(p) {
                cleanup = mountWizard(p.$el[0], opts, () => popup.close()) || cleanup;
            },
            opened(p) {
                if (window.IPTV && window.IPTV.device && window.IPTV.device.isTV) setTimeout(() => window.IPTV.tvnav.focusLayer(p.$el[0]), 80);
            },
            closed() {
                try { cleanup(); } catch (e) { /* ya limpio */ }
                try { popup.destroy(); } catch (e) { /* ignore */ }
                const el = document.getElementById(id);
                if (el && el.parentNode) el.parentNode.removeChild(el);
            }
        }
    });
    popup.open();
    return popup;
}

/** Monta el asistente en `root` (ventana modal o la página de inicio `/start/`). `opts.start`: es la primera pantalla, sin flecha de cancelar. */
export function mountWizard(root, opts, close) {
    {
        const box = root.querySelector('[data-wiz]');
        const router = () => window.app.views.main.router;
        const hasProviders = true; // Xtream y M3U se ofrecen siempre: antes se escondían hasta tener una primera fuente y quien ya tiene su IPTV no encontraba dónde ponerlo
        const groups = availableGroups({ hasProviders });
        // Editar un SMB o una carpeta del aparato: /add-source/?edit=id abre directamente sus datos.
        const found = opts.edit ? profiles.get(opts.edit) : null;
        const editing = found && !found.shared && (found.type === 'smb' || found.type === 'local' || isCloud(found.type)) ? found : null;
        const state = { cloud: {}, loopPort: 0, manual: false, step: editing ? 'form' : groups.length === 1 || (opts.group && opts.step === 'type' && groups.some((g) => g.id === opts.group)) ? 'type' : 'group', group: groups.length === 1 ? groups[0].id : (opts.group && groups.some((g) => g.id === opts.group) ? opts.group : ''), type: editing ? editing.type : '', picked: editing && editing.type === 'local' ? { uri: editing.folderUri || '', name: editing.name || '' } : null, busy: false };

        const title = () => editing ? t('settings.editProfile') : state.step === 'group' ? t('addsrc.title') : state.step === 'help' ? t('addsrc.help.title') : state.step === 'type' ? t(gk(state.group)) : t('addsrc.type.' + state.type);
        // El puerto del servidor local decide si el navegador puede volver solo a la app (las nubes tienen registrada esa dirección).
        const loop = () => !state.manual && loopbackAvailable({ port: state.loopPort, isTV: device.isTV });
        if (storageAvailable()) startBridge().then((port) => { state.loopPort = port; if (state.step === 'form' && isCloud(state.type) && !editing && !state.busy) paint(); }).catch(() => {});

        const header = () => `<div class="auth-brand">
            <img src="img/logo.png" alt=""/>
            <div><h1>${escapeHtml(title())}</h1><small>${state.step === 'group' ? t('addsrc.sub') : state.step === 'help' ? t('addsrc.help.sub') : state.step === 'type' ? t(gk(state.group, '.step')) : t('addsrc.step3')}</small></div>
        </div>`;

        const helpList = () => helpOptions({ hasProviders });
        const saveLabel = () => (state.type === 'onedrive' && !editing ? t('addsrc.onedrive.connect') : state.type === 'dropbox' && !editing && loop() ? t('addsrc.dropbox.connect') : t('addsrc.connect'));

        /* Modo tele: a pantalla completa y en tres columnas, como los ajustes del Fire TV (título · lista · detalle de lo enfocado). */
        const tvLayout = () => document.documentElement.classList.contains('fire-layout');
        const tvDetail = () => {
            const el = root.querySelector('.is-focused') || (root.contains(document.activeElement) ? document.activeElement : null);
            const card = el && el.closest ? el.closest('.wiz-card') : null;
            if (card) return `<div class="tvs-d-title">${escapeHtml((card.querySelector('.wiz-body b') || {}).textContent || '')}</div><div class="tvs-d-sub">${escapeHtml((card.querySelector('.wiz-body small') || {}).textContent || '')}</div>`;
            const field = el && el.closest ? el.closest('.field') : null;
            if (field) {
                const label = (field.querySelector('label') || {}).textContent || '';
                const help = (field.querySelector('.field-help') || {}).textContent || '';
                const about = (box.querySelector('.type-hint') || {}).textContent || '';
                return `<div class="tvs-d-title">${escapeHtml(label)}</div>${help ? `<div class="tvs-d-sub">${escapeHtml(help)}</div>` : ''}${about ? `<div class="tvs-d-hint">${escapeHtml(about)}</div>` : ''}`;
            }
            const hint = box.querySelector('.type-hint');
            return hint ? `<div class="tvs-d-sub">${escapeHtml(hint.textContent)}</div>` : '';
        };
        root.addEventListener('focusin', () => {
            const pane = box.querySelector(':scope > [data-wiz-detail]');
            if (pane) pane.innerHTML = tvDetail();
        });

        const paint = () => {
            let body = '';
            if (state.step === 'group') body = groupsHtml(groups, helpList().length > 0);
            else if (state.step === 'help') body = helpHtml(helpList());
            else if (state.step === 'type') body = state.group === 'device' ? deviceHtml() : typesHtml(state.group, availableTypes(state.group, { hasProviders }));
            else body = (isCloud(state.type) ? cloudFormHtml(state.type, editing ? { name: editing.name, folder: cloudPath(editing.host) } : {}, !!editing, loop()) : state.type === 'smb' ? smbFormHtml(editing ? Object.assign({ name: editing.name, username: editing.username, password: editing.password }, parseSmbHost(editing.host) || {}) : {}) : localFormHtml(editing ? { name: editing.name, picked: (state.picked && (state.picked.name || state.picked.uri)) || '' } : state.picked ? { name: state.picked.name, picked: state.picked.name || state.picked.uri } : {}))
                + '<div class="field-error" data-error hidden></div>'
                + `<button type="button" class="btn btn-accent btn-lg btn-block" data-save><span class="mi">check</span>${saveLabel()}</button>`;
            // Sin flecha arriba (apretaba el título): «Volver» (o «Cancelar» en la primera pantalla y al editar) como botón debajo de todo.
            // En la primera pantalla de la app no hay nada que cancelar: sin botón.
            const bottom = opts.start && state.step === 'group' ? '' : `<button type="button" class="btn btn-block wiz-bottom-back" data-focusable data-wiz-back>${t(state.step === 'group' || editing ? 'common.cancel' : 'addsrc.back')}</button>`;
            box.innerHTML = `<div class="wiz-head">${header()}</div>` + body + bottom + (tvLayout() ? '<aside class="tvs-right wiz-detail" data-wiz-detail></aside>' : '');
            const pane = box.querySelector(':scope > [data-wiz-detail]');
            if (pane) pane.innerHTML = tvDetail();
            addPasswordToggles(box);
            const first = box.querySelector('[data-focusable]:not(.wiz-bottom-back), input, button:not(.wiz-bottom-back)') || box.querySelector('.wiz-bottom-back');
            if (first && window.IPTV && window.IPTV.device && window.IPTV.device.isTV) window.IPTV.tvnav.focus(first);
        };

        /** Un paso atrás; true si lo ha dado dentro del asistente (si no, se sale de él). */
        const stepBack = () => {
            if (state.busy) { state.cancelled = true; return false; } // conectando: «Cancelar» sale del asistente (antes no hacía nada y, si el servidor tardaba, no había forma de salir)
            if (state.step === 'form' && !editing) {
                state.manual = false;
                state.step = 'type';
                state.picked = null;
                paint();
                return true;
            }
            if (state.step === 'help') {
                state.step = 'group';
                paint();
                return true;
            }
            if (state.step === 'type' && groups.length > 1) {
                state.step = 'group';
                paint();
                return true;
            }
            return false;
        };

        const showError = (msg) => {
            const el = box.querySelector('[data-error]');
            if (!el) return;
            el.textContent = msg;
            el.hidden = !msg;
        };

        let cloudAuth = null; // { type, verifier } (Dropbox) mientras se espera el código pegado

        /** Abrir Dropbox para dar el permiso: la página enseña un código que se pega después. */
        const openDropbox = async () => {
            try {
                const start = await dropboxStart(DROPBOX_KEY);
                cloudAuth = { verifier: start.verifier };
                const opened = window.IPTV.openExternal ? await window.IPTV.openExternal(start.url) : false;
                if (opened === false) window.open(start.url, '_blank');
            } catch (err) {
                const key = storageErrorKey(err);
                showError(key ? t(key) : '');
            }
        };

        /** Datos del proveedor de nube: al editar solo cambian el nombre y la carpeta; al crear, se conecta la cuenta. */
        const collectCloud = async (val) => {
            const folder = val('folder');
            if (editing) return Object.assign({}, editing, { name: val('name') || editing.name, host: cloudHost(state.type, folder) });
            let tokens;
            // Esperar en el panel: o el navegador vuelve solo a la dirección local, o (sin ella) el código de dispositivo de Microsoft.
            const panel = box.querySelector('[data-device]');
            const waiting = async (showCode, run) => {
                panel.hidden = false;
                box.querySelector('[data-device-codebox]').hidden = !showCode;
                state.cloud.cancel = { stop: false };
                try { return await run(state.cloud.cancel); } finally { panel.hidden = true; state.cloud.cancel = null; }
            };
            const tick = (left) => { const w = box.querySelector('[data-device-wait]'); if (w) w.textContent = t('addsrc.onedrive.waiting', { min: Math.max(1, Math.ceil(left / 60)) }); };
            const open = async (url) => { const opened = window.IPTV.openExternal ? await window.IPTV.openExternal(url) : false; if (opened === false) window.open(url, '_blank'); };
            if (state.type === 'dropbox') {
                if (loop()) {
                    const redirectUri = loopbackUri('dropbox', state.loopPort);
                    const st = randomState();
                    const start = await dropboxStart(DROPBOX_KEY, { redirectUri, state: st });
                    const code = await waiting(false, async (cancel) => { await open(start.url); return waitForCallback(state.loopPort, st, { cancel, onTick: tick }); });
                    tokens = await dropboxExchange(DROPBOX_KEY, start.verifier, code, fetch, redirectUri);
                } else {
                    if (!cloudAuth) throw Object.assign(new Error('code'), { code: 'code' });
                    tokens = await dropboxExchange(DROPBOX_KEY, cloudAuth.verifier, val('code'));
                }
            } else if (loop()) {
                const redirectUri = loopbackUri('onedrive', state.loopPort);
                const st = randomState();
                const start = await msAuthStart(MS_CLIENT_ID, { redirectUri, state: st });
                const code = await waiting(false, async (cancel) => { await open(start.url); return waitForCallback(state.loopPort, st, { cancel, onTick: tick }); });
                tokens = await msAuthExchange(MS_CLIENT_ID, start.verifier, code, redirectUri);
            } else {
                const start = await msDeviceStart(MS_CLIENT_ID);
                box.querySelector('[data-device-code]').textContent = start.userCode;
                box.querySelector('[data-device-url]').textContent = start.url.replace(/^https?:\/\//, '');
                tokens = await waiting(true, async (cancel) => {
                    if (window.IPTV.openExternal) window.IPTV.openExternal(start.url);
                    return msDeviceWait(MS_CLIENT_ID, start, { cancel, onStatus: (st, e) => { const w = box.querySelector('[data-device-state]'); if (w) w.textContent = st === 'pending' ? t('addsrc.onedrive.checking') : st === 'approved' ? t('addsrc.onedrive.approved') : (e && e.detail) || ''; }, onTick: tick });
                });
            }
            const account = state.type === 'dropbox' ? await dropboxAccount(tokens.accessToken) : await msAccount(tokens.accessToken);
            return { type: state.type, name: val('name') || account.email || (state.type === 'dropbox' ? 'Dropbox' : 'OneDrive'), host: cloudHost(state.type, folder), username: account.email, password: tokens.refreshToken };
        };

        const save = async () => {
            if (state.busy) return;
            const val = (name) => ((box.querySelector('[name="' + name + '"]') || {}).value || '').trim();
            const btn = box.querySelector('[data-save]');
            state.busy = true;
            btn.disabled = true;
            btn.innerHTML = `<div class="spinner" style="width:20px;height:20px;border-width:2px"></div>${t('login.connecting')}`;
            showError('');
            try {
                let data;
                if (isCloud(state.type)) data = await collectCloud(val);
                else if (state.type === 'smb') {
                    const host = smbHost({ server: val('server'), share: val('share'), path: val('path') });
                    data = { type: 'smb', name: val('name') || val('share'), host, username: val('username'), password: (box.querySelector('[name="password"]') || {}).value || '' };
                } else {
                    if (!state.picked) throw Object.assign(new Error('pick'), { code: 'pick' });
                    data = { type: 'local', name: val('name') || state.picked.name, host: editing ? editing.host : newLocalHost(), folderUri: state.picked.uri, username: '', password: '' };
                }
                data = prepareProvider(data);
                await verifyProvider(data, { editingId: editing && editing.id });
                if (state.cancelled) return; // cerró el asistente mientras se comprobaba: no se guarda nada
                const profile = editing ? await profiles.update(editing.id, data) : await profiles.add(data);
                // Una fuente nueva se suma a las que ya hay; una editada se vuelve a leer entera.
                const joined = editing ? (profiles.setCurrent(profile.id), false) : profiles.joinCatalog(profile.id);
                notify.toast(t('login.success'), { icon: 'check_circle' });
                // La fuente que se suma a otra ya tiene su ficha guardada: su catálogo (puede ser enorme) se descarga en segundo plano, sin dejar el asistente colgado en «Conectando…».
                if (joined) window.IPTV.catalog.loadExtras({ force: true }).catch(() => {});
                else await window.IPTV.actions.activateProfile(profile, { force: !!editing });
                close();
                router().navigate('/', { reloadAll: true, clearPreviousHistory: true });
            } catch (err) {
                console.warn('[add-source] error', err);
                const key = storageErrorKey(isCloud(state.type) && err ? { code: err.code, status: err.status, cloud: true } : err);
                // Si el servicio explicó el motivo (p. ej. «invalid_grant: …»), se enseña entre corchetes para poder localizarlo.
                showError(key ? t(key) + (err && err.detail ? ' [' + err.detail + ']' : '') : '');
                state.busy = false;
                btn.disabled = false;
                btn.innerHTML = `<span class="mi">check</span>${saveLabel()}`;
            }
        };

        const pick = async () => {
            try {
                const folder = await storageBridge.pickFolder();
                if (!folder) return;
                state.picked = folder;
                box.querySelector('[data-picked]').hidden = false;
                box.querySelector('[data-picked-name]').textContent = folder.name || folder.uri;
                const nameInput = box.querySelector('[name="name"]');
                if (nameInput && !nameInput.value) nameInput.value = folder.name || '';
                showError('');
            } catch (err) {
                showError(t('addsrc.err.generic'));
            }
        };

        box.addEventListener('click', (e) => {
            if (e.target.closest('[data-pick-direct]')) {
                storageBridge.pickFolder().then((folder) => {
                    if (!folder) return;
                    state.type = 'local';
                    state.picked = folder;
                    state.step = 'form';
                    paint();
                }).catch(() => { /* sin selector (algunas teles): se queda donde está */ });
                return;
            }
            if (e.target.closest('[data-help-open]')) { state.step = 'help'; paint(); return; }
            const hp = e.target.closest('[data-help]');
            if (hp) {
                const h = helpList().find((x) => x.id === hp.getAttribute('data-help'));
                if (!h) return;
                state.group = h.group;
                if (h.type) {
                    state.type = h.type;
                    if (isClassic(h.type)) { close(); router().navigate('/login/?type=' + encodeURIComponent(h.type) + '&wizard=1'); }
                    else { state.step = 'form'; paint(); }
                } else { state.step = 'type'; paint(); }
                return;
            }
            const g = e.target.closest('[data-group]');
            if (g) { state.group = g.getAttribute('data-group'); state.step = 'type'; paint(); return; }
            const ty = e.target.closest('[data-type]');
            if (ty) {
                state.type = ty.getAttribute('data-type');
                if (isClassic(state.type)) { close(); router().navigate('/login/?type=' + encodeURIComponent(state.type) + '&wizard=1'); }
                else { state.step = 'form'; paint(); }
                return;
            }
            if (e.target.closest('[data-wiz-back]')) { if (!stepBack()) close(); return; }
            if (e.target.closest('[data-pick]')) { pick(); return; }
            if (e.target.closest('[data-cloud-open]')) { openDropbox(); return; }
            if (e.target.closest('[data-cloud-manual]')) { state.manual = true; paint(); return; }
            if (e.target.closest('[data-device-cancel]')) { if (state.cloud.cancel) state.cloud.cancel.stop = true; return; }
            if (e.target.closest('[data-save]')) save();
        });
        box.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && e.target && e.target.tagName === 'INPUT' && state.step === 'form') { e.preventDefault(); save(); }
        });

        paint();
        // La tecla «atrás» (y el botón del móvil) primero retrocede un paso dentro del asistente (ver back() en main.js).
        window.IPTV.pageBack = stepBack;
        return () => {
            if (window.IPTV.pageBack === stepBack) window.IPTV.pageBack = null;
        };
    }
}
